// CREATE TRIGGER slot recognizers (`ddl-trigger-event` /
// `ddl-create-trigger-timing`) — split out of `editorContextSlot.ddl2.ts`
// (plan19 T7 fix round, review Q3: keep the family file under the ~200-line
// budget along a clean seam). G11: pure token scan, NO web-tree-sitter.
//
// PORTED-FAITHFUL to the retired string-walk `classifyDdlPositionKind`
// (review S2/Q1 resolution: PORT, not adjudicate):
//   - The event slot accepts only INSERT / UPDATE / DELETE partials of >= 3
//     chars (`INS`, `UPDA`, `DELETE`, …) after BEFORE/AFTER/INSTEAD OF — the
//     retired regex's `INS(?:E(?:R(?:T)?)?)? | UPD… | DEL…` vocabulary.
//     TRUNCATE (a legal PG trigger event) was ABSENT from that vocabulary and
//     stays unrecognized here; widening it is a product improvement for a
//     future slice, not this migration.
//   - A bare timing-keyword tail the cursor is still ATTACHED to
//     (`… BEFORE▮`, `… INSTEAD OF▮`) does NOT fire — the retired regex
//     required `\s+` after the timing keyword. With a separating space
//     (`… BEFORE ▮`) it fires. Rationale: mid-word the user is completing the
//     keyword itself; firing the event slot would hide that completion.
//   - The TIMING slot's head form omits CONSTRAINT: the retired timing regex
//     accepted `CREATE [OR REPLACE] [TEMP[ORARY]] TRIGGER <name>` only, while
//     the event slot's head gate also accepted CONSTRAINT — so
//     `CREATE CONSTRAINT TRIGGER trg ▮` is null (timing) but
//     `CREATE CONSTRAINT TRIGGER trg AFTER ▮` fires the event slot.

import type { ClauseToken } from './editorContextClause';
import { dropAttachedPartial, upper } from './editorContextSlotShared';

export type TriggerSlotKind =
  | 'ddl-trigger-event'
  | 'ddl-create-trigger-timing'
  | 'ddl-target-table-column';

// `ALTER` joins the ported noise words for T-SQL's `CREATE OR ALTER TRIGGER`
// (the one head form the retired PG/MySQL/Oracle/SQLite-only string-walk never
// had to parse). It cannot change a ported answer: `CREATE ALTER TRIGGER` is
// not legal in any of the four flip dialects, so no reachable statement gains
// or loses a slot from it.
const EVENT_HEAD_NOISE: ReadonlySet<string> = new Set([
  'OR', 'REPLACE', 'ALTER', 'TEMP', 'TEMPORARY', 'CONSTRAINT',
]);
const TIMING_HEAD_NOISE: ReadonlySet<string> = new Set(['OR', 'REPLACE', 'TEMP', 'TEMPORARY']);

/**
 * Classify the CREATE TRIGGER slot at the cursor, or `null`.
 * `cursorOffset` distinguishes an attached bare-keyword tail (no fire) from a
 * whitespace-separated slot (fire) — see the port notes in the header.
 */
export function triggerSlot(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): TriggerSlotKind | null {
  if (!headIsCreateTrigger(tokens, EVENT_HEAD_NOISE)) return null;
  if (followsTriggerEvent(tokens, cursorOffset)) return 'ddl-trigger-event';
  if (followsUpdateOfColumnList(tokens, cursorOffset)) return 'ddl-target-table-column';
  if (
    headIsCreateTrigger(tokens, TIMING_HEAD_NOISE) &&
    (followsTriggerName(tokens) || followsPartialTimingKeyword(tokens, cursorOffset))
  ) {
    return 'ddl-create-trigger-timing';
  }
  return null;
}

/** The timing vocabulary this slot offers — a caret attached to a PARTIAL of
 *  one of these is still the timing slot. `INSTEAD OF` contributes both words
 *  so `CREATE TRIGGER t INS▮` / `… INSTEAD O▮` stay in. */
const TIMING_KEYWORDS: readonly string[] = ['BEFORE', 'AFTER', 'INSTEAD', 'OF'];

/**
 * TYPED-PREFIX PARITY: `CREATE TRIGGER t BEF▮` is the same timing slot as
 * `CREATE TRIGGER t ▮` — the half-typed word is the timing keyword the user is
 * answering with, so the trigger-name scan is re-run with that word dropped.
 * Without it the caret fell through to `null` and the resolver answered with
 * the whole keyword vocabulary on the first keystroke (oracle coverage
 * script.36 §1, pgsql script.35 §6).
 *
 * Two guards keep the ported answers byte-identical:
 *   - the dropped word must be a STRICT prefix of a timing keyword. A
 *     COMPLETE one (`… BEFORE▮`, `… INSTEAD OF▮`) is pinned to `null` by
 *     the equivalence corpus — the retired regex required `\s+` after the
 *     timing keyword — and a word that is no prefix at all is a trigger NAME
 *     the raw scan already answers for (`CREATE TRIGGER my_tr▮`).
 *   - the remaining stream must still end at the trigger name.
 */
function followsPartialTimingKeyword(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  const dropped = dropAttachedPartial(tokens, cursorOffset);
  if (dropped.length === tokens.length) return false;
  const partial = upper(tokens[tokens.length - 1]);
  if (partial === null) return false;
  const isStrictPrefix = TIMING_KEYWORDS.some(
    (kw) => kw.length > partial.length && kw.startsWith(partial),
  );
  if (!isStrictPrefix) return false;
  return followsTriggerName(dropped);
}

/**
 * T-SQL arm of the event slot. mssql does NOT run `classifyDdlSlot` (it never
 * consumed the retired string-walk), and its trigger grammar puts the TIMING
 * keyword AFTER the target table (`CREATE TRIGGER t ON tbl AFTER ▮`), so the
 * TIMING slot — which fires right after the trigger NAME — would be wrong
 * there. The dispatcher calls this narrow entry point on the mssql arm so the
 * engine gains the event vocabulary without the PG-shaped timing answer.
 */
export function triggerEventSlot(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  if (!headIsCreateTrigger(tokens, EVENT_HEAD_NOISE)) return false;
  return followsTriggerEvent(tokens, cursorOffset);
}

/**
 * Tail is a `CREATE TRIGGER … UPDATE OF ▮` column list — the optional column
 * restriction of an UPDATE trigger event (`BEFORE UPDATE OF a, b ON tbl`).
 * Accepts the bare slot (`UPDATE OF ▮`), a partial column the caret is still
 * attached to (`UPDATE OF Unit▮`), and any later slot in the list
 * (`UPDATE OF a, ▮`).
 *
 * The list's owning table is named by the `ON <tbl>` clause that comes AFTER
 * the caret, which this backward token scan cannot see — that is why the slot
 * maps to `'ddl-target-table-column'` (look-ahead is the resolver's job)
 * rather than to `'ddl-index-column'`, whose resolvers all bind their target
 * from text BEFORE the caret.
 *
 * PG, Oracle and SQLite all spell the restriction `UPDATE OF <cols>`. MySQL
 * and T-SQL have no such list, so the shape is unreachable there.
 */
function followsUpdateOfColumnList(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  let i = tokens.length - 1;
  // Step over an attached partial column name (`UPDATE OF Unit▮`).
  if (i >= 0 && tokens[i]!.kind === 'identifier' && upper(tokens[i]) !== 'OF') i -= 1;
  // Step back over any already-typed `<col> ,` pairs of the list.
  while (i >= 0 && tokens[i]!.kind === 'comma') {
    i -= 1;
    if (i >= 0 && tokens[i]!.kind === 'identifier') i -= 1;
    else return false;
  }
  if (upper(tokens[i]) !== 'OF') return false;
  // A bare `OF` at the very tail must be whitespace-separated from the caret
  // (`OF▮` is still the keyword being typed) — the same rule the timing/event
  // tails follow.
  if (i === tokens.length - 1 && tokens[i]!.end >= cursorOffset) return false;
  return upper(tokens[i - 1]) === 'UPDATE';
}

/** Head scan for a `CREATE [<noise>…] TRIGGER` lead (`noise` = the optional
 *  keywords the caller's slot accepts between CREATE and TRIGGER). */
function headIsCreateTrigger(tokens: readonly ClauseToken[], noise: ReadonlySet<string>): boolean {
  for (let i = 0; i < tokens.length; i += 1) {
    if (upper(tokens[i]) !== 'CREATE') continue;
    let j = i + 1;
    while (j < tokens.length && noise.has(upper(tokens[j]) ?? '')) j += 1;
    if (upper(tokens[j]) === 'TRIGGER') return true;
  }
  return false;
}

/** True when `word` is a >=3-char prefix of INSERT / UPDATE / DELETE (the
 *  retired regex's exact partial vocabulary — full keywords included, TRUNCATE
 *  deliberately excluded). */
function isEventVerbPartial(word: string | null): boolean {
  if (word === null || word.length < 3) return false;
  return (
    'INSERT'.startsWith(word) || 'UPDATE'.startsWith(word) || 'DELETE'.startsWith(word)
  );
}

/** Tail is `BEFORE` / `AFTER` / `INSTEAD OF` (whitespace-separated from the
 *  cursor) or the same followed by an INS/UPD/DEL event-verb partial. */
function followsTriggerEvent(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  let i = tokens.length - 1;
  let steppedPartial = false;
  const u0 = upper(tokens[i]);
  if (u0 !== 'BEFORE' && u0 !== 'AFTER' && u0 !== 'OF') {
    // Step over a trailing event-verb partial (INS / UPDA / DELETE / …) —
    // partials may sit attached to the cursor (`BEFORE INS▮`), matching the
    // retired regex's optional trailing group.
    if (!isEventVerbPartial(u0)) return false;
    i -= 1;
    steppedPartial = true;
  }
  // A bare timing keyword at the very tail must be whitespace-separated from
  // the cursor (`BEFORE ▮` fires, `BEFORE▮` does not — the user is still
  // typing the keyword; the retired regex required `\s+` here).
  if (!steppedPartial && tokens[i] !== undefined && tokens[i]!.end >= cursorOffset) {
    return false;
  }
  const u = upper(tokens[i]);
  if (u === 'BEFORE' || u === 'AFTER') return true;
  if (u === 'OF') return upper(tokens[i - 1]) === 'INSTEAD';
  return false;
}

/** Tail is `TRIGGER <name>` (cursor at/after the trigger name — the timing
 *  slot; the retired regex fired on the attached name too). */
function followsTriggerName(tokens: readonly ClauseToken[]): boolean {
  let i = tokens.length - 1;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
  i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
    i -= 1;
  }
  return upper(tokens[i]) === 'TRIGGER';
}
